'use client';

import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

const MONO_FONT = 'var(--font-plex-mono), IBM Plex Mono, monospace';

interface MarketButtonProps {
  label: string;
  price: string;
  line?: string | number;
  onClick: (e: React.MouseEvent) => void;
  active?: boolean;
  disabled?: boolean;
  color?: string;
  priceChangeDirection?: 'up' | 'down' | null;
  lastUpdateTimestamp?: number;
  sx?: SxProps<Theme>;
}

/**
 * MarketButton Component
 * A styled button used for displaying betting outcomes (price and label)
 * within the sports game cards.
 */
export const MarketButton = ({
  label,
  price,
  onClick,
  line = '',
  active = false,
  disabled = false,
  color,
  priceChangeDirection,
  lastUpdateTimestamp,
  sx,
}: MarketButtonProps) => {
  const theme = useTheme();
  // Resolve the fallback through the theme so callers that don't pass a color
  // (e.g. a generic yes/no outcome with no team/over-under color assigned)
  // still get a real CSS color for bgcolor/borderColor when active.
  const resolvedColor = color || theme.palette.primary.main;

  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      disableRipple
      sx={{
        width: { xs: 130, sm: 130, md: 130 },
        height: { xs: 42, sm: 42 },
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 1,
        alignItems: 'center',
        borderRadius: '10px',
        bgcolor: active ? alpha(resolvedColor, 0.5) : 'action.selected',
        color: active ? '#ffffff' : 'text.primary',
        px: 1.5,
        py: { xs: 0.5, sm: 1 },
        border: '1px solid',
        borderColor: active ? resolvedColor : 'divider',
        ...(Array.isArray(sx) ? sx : [sx]),
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          // `action.hover` is a lighter overlay than the `action.selected` resting
          // bg above, so hovering used to look no darker (or even lighter) than
          // resting. Step darker explicitly instead, for both active and inactive.
          bgcolor: active
            ? alpha(resolvedColor, 0.65)
            : alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.24 : 0.14),
          borderColor: active ? resolvedColor : 'divider',
        },
        // A `---` button has no market to trade — don't let it look or act
        // clickable, since clicking it would otherwise silently fall back to
        // whatever handler the caller passes for a "no data" click.
        '&.Mui-disabled': {
          bgcolor: 'action.selected',
          color: 'text.disabled',
          borderColor: 'divider',
          opacity: 0.55,
        },
      }}
    >
      {priceChangeDirection && lastUpdateTimestamp && (
        <Box
          key={lastUpdateTimestamp}
          sx={{
            position: 'absolute',
            right: 4,
            bottom: priceChangeDirection === 'down' ? 4 : 'auto',
            top: priceChangeDirection === 'up' ? 4 : 'auto',
            rotate: priceChangeDirection === 'up' ? '0deg' : '90deg',
            width: 8,
            height: 8,
            borderTop: '2px solid',
            borderRight: '2px solid',
            borderColor: active ? '#ffffff' : resolvedColor,
            opacity: 0.9,
            // Stay solid for 4s, then fade out over 1s
            animation: 'indicatorFadeOut 5s forwards',
            '@keyframes indicatorFadeOut': {
              '0%': { opacity: 1, transform: 'scale(1)' },
              '80%': { opacity: 1, transform: 'scale(1)' },
              '100%': { opacity: 0, transform: 'scale(0.5)' },
            },
          }}
        />
      )}

      <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: MONO_FONT,
            fontSize: { xs: '1rem', md: '0.7rem' },
            fontWeight: { xs: 300, lg: 600 },
            letterSpacing: '0.02em',
            opacity: 0.8,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
        {line && (
          <Typography
            sx={{
              fontFamily: MONO_FONT,
              fontSize: { xs: '0.55rem', sm: '0.6rem' },
              fontWeight: 600,
              opacity: 0.8,
            }}
          >
            {line || '---'}
          </Typography>
        )}
      </Stack>

      <Typography
        sx={{
          fontFamily: MONO_FONT,
          fontSize: { xs: '0.8rem', sm: '1.05rem' },
          fontWeight: 700,
          ml: { xs: 0.25, sm: 0.5 },
          whiteSpace: 'nowrap',
        }}
      >
        {price || '---'}
      </Typography>
    </Button>
  );
};

export default MarketButton;
